/**
 * ReferenceImagePicker - Pick reference images from the Media Library.
 *
 * The picker opens via "Add from Media Library", the user multi-selects
 * images, and on confirm each new id is added to the store. Names are
 * assigned by the store ("ref-image-1", "ref-image-2", ...) —
 * the post-pick naming step was intentionally removed to simplify the
 * flow. The Media Library picker itself remains the only modal in this
 * path.
 */
import { useCallback, useMemo, useState } from 'react';
import { useCreativeStudioStore } from '@/store/creativeStudioStore';
import type { NamedReferenceImage } from '@/store/creativeStudioStore';
import { useMediaImage } from '@/hooks/queries/useMediaQueries';
import { ImagePicker } from '@/components/media/ImagePicker';
import { PlusIcon } from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { ImageThumbnailWithActions } from '@/components/common/ImageThumbnailWithActions';
import '@/styles/tooltip.css';

interface ReferenceImagePickerProps {
  disabled?: boolean;
}

interface ReferenceThumbnailProps {
  image: NamedReferenceImage;
  index: number;
  onRemove: () => void;
  disabled?: boolean;
}

function ReferenceThumbnail({
  image,
  index,
  onRemove,
  disabled,
}: ReferenceThumbnailProps) {
  const { data: mediaImage, isLoading } = useMediaImage(image.id);
  const thumbnailUrl = mediaImage?.thumbnail_url || mediaImage?.signed_url;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center w-16">
        <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
          <div className="w-full h-full flex items-center justify-center">
            <div className="animate-pulse w-8 h-8 bg-gray-200 rounded" />
          </div>
          <div className="absolute bottom-0.5 left-0.5 px-1 py-0.5 bg-black/60 rounded text-[10px] text-white font-medium">
            {index + 1}
          </div>
        </div>
        <div className="mt-1 h-3 w-12 bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-16">
      <ImageThumbnailWithActions
        imageUrl={thumbnailUrl}
        alt={mediaImage?.original_filename || image.name}
        size={{ width: 64, height: 64 }}
        onRemove={onRemove}
        removeLabel={`Remove reference image ${image.name}`}
        confirmTitle="Remove Reference Image"
        confirmMessage="Are you sure you want to remove this reference image?"
        disabled={disabled}
        label={String(index + 1)}
      />
      <span
        className="mt-1 text-[11px] text-gray-600 truncate max-w-[64px] text-center"
        title={image.name}
      >
        {image.name}
      </span>
    </div>
  );
}

export function ReferenceImagePicker({ disabled }: ReferenceImagePickerProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const {
    referenceImages,
    setReferenceImages,
    addReferenceImage,
    removeReferenceImage,
  } = useCreativeStudioStore();

  const selectedIds = useMemo(() => referenceImages.map((r) => r.id), [referenceImages]);
  const maxReached = referenceImages.length >= 14;

  const handlePickerConfirm = useCallback(
    (selectedFromPicker: string[]) => {
      // Apply deselections first: drop any current ref that the picker no
      // longer includes. Then add the newly-selected ids — the store assigns
      // each one the next sequential `ref-image-N` slot.
      const existingIds = new Set(selectedIds);
      const newIds = selectedFromPicker.filter((id) => !existingIds.has(id));
      const keptInOrder = referenceImages.filter((r) =>
        selectedFromPicker.includes(r.id)
      );
      setReferenceImages(keptInOrder);
      for (const id of newIds) {
        addReferenceImage(id);
      }
      setIsPickerOpen(false);
    },
    [referenceImages, selectedIds, setReferenceImages, addReferenceImage]
  );

  return (
    <div>
      {/* Thumbnails + a trailing "+" tile. The tile matches the 64x64
          thumbnail dimensions so the row reads as one strip of equivalent
          slots. Hover/focus surfaces the longer "Add from Media Library"
          label via the shared tooltip CSS. */}
      <div className="flex flex-wrap gap-3">
        {referenceImages.map((ref, index) => (
          <ReferenceThumbnail
            key={ref.id}
            image={ref}
            index={index}
            onRemove={() => removeReferenceImage(ref.id)}
            disabled={disabled}
          />
        ))}
        <button
          type="button"
          onClick={() => setIsPickerOpen(true)}
          disabled={disabled || maxReached}
          aria-label={maxReached ? 'Maximum reached (14)' : 'Add from Media Library'}
          className="relative w-16 h-16 inline-flex items-center justify-center
                     border border-dashed border-gray-300 rounded-lg
                     text-gray-500 hover:border-gray-400 hover:text-gray-700
                     transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlusIcon className="h-6 w-6" />
          {/* Info icon pinned to the upper-right corner of the tile.
              Purely decorative — the button's `aria-label` already conveys
              "Add from Media Library" / "Maximum reached" to screen readers
              and on keyboard focus, so the tooltip is mouse-only sugar.
              We mark the icon wrapper `aria-hidden` and skip the
              `aria-describedby`/`role="tooltip"` plumbing that was previously
              wired to a non-focusable `<span>` (the tooltip never reached
              keyboard users via `:focus-within` and the linkage was dead
              weight for screen readers). Hovering only this small icon
              keeps surfacing the tooltip — hovering the rest of the button
              stays quiet so the label doesn't keep popping over the user.
              Two wrappers because `tooltip-container` sets
              `position: relative`, which would override `absolute` if they
              shared a single element. */}
          <span aria-hidden="true" className="absolute top-0.5 right-0.5">
            <span className="tooltip-container">
              <span className="block text-gray-400">
                <InformationCircleIcon className="h-3.5 w-3.5" />
              </span>
              <span
                className="tooltip-text tooltip-text-right"
                style={{ whiteSpace: 'normal', width: '12rem', maxWidth: '60vw', textAlign: 'left' }}
              >
                {maxReached ? 'Maximum reached (14)' : '+ Add from Media Library'}
              </span>
            </span>
          </span>
        </button>
      </div>

      {/* Image Picker Modal */}
      <ImagePicker
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        selectedImageIds={selectedIds}
        onConfirm={handlePickerConfirm}
        maxSelection={14}
      />
    </div>
  );
}
